/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { SearchAutocomplete } from '@/shared/ui/components/form/SearchAutocomplete';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';

interface DefinePropertyDialogProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
  /** The deal's current property, pre-selected when there is one. */
  initialPropertyId?: string | null;
  onSubmit: (propertyId: string) => Promise<void>;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * Dialog attaching a property to an attendance that started without one.
 *
 * The property alone, on purpose: attaching one is what a lead needs to
 * become an attendance somebody is working, and asking for anything
 * else at that moment would be asking the broker to fill a form to
 * record one fact. The type lives in "Editar atendimento", where the
 * offers that govern it can be taken into account.
 *
 * @param props - Dialog state, the agency, and the submit callback.
 * @returns The define-property form shell.
 */
export function DefinePropertyDialog({
  open,
  onClose,
  agencyId,
  initialPropertyId = null,
  onSubmit,
}: DefinePropertyDialogProps) {
  const { t } = useTranslation('features/deals');
  const [propertyId, setPropertyId] = useState<string | null>(initialPropertyId);
  const [submitting, setSubmitting] = useState(false);
  const { properties } = useAgencyProperties(open ? agencyId : undefined);

  useEffect(() => {
    if (open) setPropertyId(initialPropertyId);
  }, [open, initialPropertyId]);

  const options = properties.map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel }));

  const handleSubmit = async () => {
    if (!propertyId) return;
    setSubmitting(true);
    try {
      await onSubmit(propertyId);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('detail.definePropertyDialog.title')}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={handleSubmit}
          disabled={submitting || !propertyId}
          loading={submitting}
          label={t('detail.definePropertyDialog.confirm')}
          savingLabel={t('detail.definePropertyDialog.confirming')}
        />
      }
    >
      <div data-testid="define-property-form" className="flex flex-col gap-(--space-4)">
        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('detail.definePropertyDialog.property')}
          </Text>
          <SearchAutocomplete
            options={options}
            value={propertyId}
            onChange={setPropertyId}
            placeholder={t('detail.definePropertyDialog.propertyPlaceholder')}
          />
        </fieldset>
        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
          {t('detail.definePropertyDialog.hint')}
        </Text>
      </div>
    </AdaptiveFormShell>
  );
}
